<!--
@component Container (wrapper) for any control or a group of control elements.

   Main properties:
   - `label` - the label text (optional).
   - `name` - name of the element, will be used for add CSS class.
   - `status` - status name will be added as a class (optional).
   - `colors` - JSON with css variables defining colors of control elements.
   - `labelWidth` - width of label elements in characters, default: 13.

   **Description**

   If `label` is not empty the label will be shown as fixed width text on the left of the
   container, so the control elements will be indendent. If no or empty label is provided, the
   control elements will be placed at the beginning of the container.

   JSON with colors can be exported from the package (plus see readme for details).

   If several containers share the same parent, the first and the last ones will have slightly
   round borders. There will be a small marging (0.5px) between the containers in the stack.

   You can also put any text inside container, tags `<p>` and `<h2>` are will be styled nicely.

   If you provide status `'error'` the container will be shown using a specific warning color
   (by default crimson) assuming that it contains an error message.

   **Example:**

```svelte
<script>
   import {Container, Range, Button} from 'svelte-controls-basic';
   let rangeValue = $state(0);
</script>

<Container {colors}>
   <h2>Block</h2>
</Container>
<Container label="range" {colors}>
   <Range bind:value={rangeValue} min={0} max={0.1} decNum={2}/>
</Container>
<Container label="button" {colors}>
   <Button text="click me" onclick={() => alert(rangeValue)}/>
</Container>
{#if rangeValue > 0.07}
<Container status="error">
    <p>The value is too high, reduce it please!</p>
</Container>
{/if}
```
-->
<script>
   import { Colors } from './utils';
   let {
      label = null,
      name = '',
      status = '',
      colors = '',
      labelWidth = 12,
      children
   } = $props();
</script>

<div class="control-element {name} {status}" style={colors}>
   {#if label}
   <span class="label" style="flex-basis:{labelWidth}ch;">{label}</span>
   {/if}
   {@render children?.()}
</div>

<style>
   .control-element {
      font-size: 0.85em;
      background: var(--container-bg-color, #fff);
      color: var(--text-color-dark, #606570);

      padding: 0.5em 0.5em 0.5em 1.0em;
      border-radius: 0;
      display: flex;
      justify-items: flex-start;
      justify-content: flex-start;
      align-items: center;
      margin-bottom: 0.5px;
   }

   .control-element:hover {
      background: var(--container-bg-color, #fafdff);
   }

   .control-element:first-child {
      margin-top: 0.25rem;
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
   }

   .control-element:last-child {
      margin-bottom: 0.25rem;
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
   }

   .hidden {
      display: none;
   }

   .control-element.error {
      background: var(--warning-color, crimson);
      color: var(--warning-text-color, #fefefe);
   }


   .label {
      padding-right: 0.75em;
      flex-grow: 0;
      flex-shrink: 1;
      color: var(--text-color-dark, #606570);
   }

   .control-element > :global(.wrapper) {
      flex: 1 1 auto;
      position: relative;
      box-sizing: border-box;
      display: inline-block;
      margin: 0;
      padding: 0;
      width: auto;
      align-items: flex-start;
      justify-content: center;
      justify-items: flex-start;
   }

   .control-element > :global(h2) {
      text-align: left;
      flex-grow: 1;
      padding: 0 0.25em 0 0;
      margin: 0;
      font-size: 1.1em;
   }

   .control-element > :global(p) {
      text-align: left;
      flex-grow: 1;
      padding: 0 0.25em 0 0em;
      margin: 0;
      font-size: max(12px, 0.9em);
   }

   :global(.control-element.error p) {
      color: var(--warning-text-color, #fefafa);
   }

   :global(.control-element.error a) {
      color: var(--warning-link-color, #ffeeaa);
   }

   :global(.control-element.error a:hover) {
      color: var(--warning-link-hover-color, #ffee80);
   }

   .control-element > :global(.title) {
      font-size: 1.05em;
      font-weight: bold;
      color: var(--text-color-dark, #606570);
      flex-grow: 1;
      padding: 0.3em 0;
      margin: 0;
   }

   /* children elements: range slider */

   .control-element > :global(.range-slider-container) {
      outline-color: var(--outline-color, #ccc);
      background: var(--bg-color-light, #f0f0f0);
   }

   .control-element > :global(.range-slider-container:focus-visible) {
      outline-color: var(--outline-color, #ccc);
   }

   .control-element :global(.range-slider) {
      color: var(--text-color-light, #fafafa);
      background-color: var(--main-color1, #6eb8ff);
      border-right-color: var(--main-color2, #528abe);
   }


   .control-element :global(.range-value) {
      color: var(--text-color-light, #fafafa);
   }

   .control-element :global(.range-value_left) {
      color: var(--text-color-dark, #606570);
   }

   .control-element :global(.range-value_right) {
      color: var(--text-color-dark, #606570);
   }


   /* children elements: button */

   .control-element :global(.button) {
      border-color: transparent;
      background-color: var(--bg-color-dark, #606570);
      color: var(--text-color-light, #fafafa);
      outline-color: var(--outline-color, #ccc);
   }

   .control-element :global(.button:hover) {
      border-color: var(--main-color1, #6eb8ff);
      background-color: var(--main-color1-light, #6eb8ff20);
      color: var(--main-color2, #5086b9);
   }



   /* children elements: button-cancel */

   .control-element :global(.button-cancel) {
      background-image:
         url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" version="2.0" viewBox="0 0 100 100" style="stroke:%23fafafa;stroke-width:1em;" ><line x1="0" y1="0" x2="100" y2="100"/><line x1="0" y1="100" x2="100" y2="0"/></svg>');
   }

   .control-element :global(.button-cancel:hover) {
      background-color: var(--warning-color, #dc143c);
   }

   /* children elements: button-undo */

   .control-element :global(.button-undo) {
      background-image:
         url('data:image/svg+xml;utf8,<svg width="99px" height="99px" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g stroke-linecap="round" stroke-linejoin="round" ><path d="M6 7L7 6L4.70711 3.70711L5.19868 3.21553C5.97697 2.43724 7.03256 2 8.13323 2C11.361 2 14 4.68015 14 7.93274C14 11.2589 11.3013 14 8 14C6.46292 14 4.92913 13.4144 3.75736 12.2426L2.34315 13.6569C3.90505 15.2188 5.95417 16 8 16C12.4307 16 16 12.3385 16 7.93274C16 3.60052 12.4903 0 8.13323 0C6.50213 0 4.93783 0.647954 3.78447 1.80132L3.29289 2.29289L1 0L0 1V7H6Z" fill="%23fafafa"></path> </g></svg>');
   }

   .control-element :global(.button-undo:hover) {
      background-color: var(--main-color1,#6eb8ff);
   }

   /* children elements: button-download */
   .control-element :global(.button-download) {
      background-image:
         url('data:image/svg+xml;utf8,<svg viewBox="0 0 110 110" style="stroke:%23fafafa;stroke-width:1.1em;" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><line x1="50" y1="0" x2="50" y2="100"/><line x1="10" y1="60" x2="50" y2="100"/><line x1="90" y1="60" x2="50" y2="100"/></svg>');
   }

   .control-element :global(.button-download:hover) {
      background-color: var(--main-color1,#6eb8ff);
   }

   /* select */
   .control-element :global(.selector:focus-visible){
      outline: 2px solid;
      outline-offset: 2px;
      outline-color: var(--outline-color, #ccc);
   }

   .control-element :global(.selector > .option){
      color: var(--text-color-dark, #606570);
      background-color: var(--bg-color-light, #f0f0f0);
   }

   .control-element :global(.plot-selector > .option > svg){
      stroke: var(--main-color1, #606570);
   }

   .control-element :global(.plot-selector > .option.selected > svg){
      stroke: var(--text-color-light, #fefefe);
   }

   .control-element :global(.selector > .option.selected) {
      background-color: var(--main-color1, #6eb8ff);
      color: var(--text-color-light, #fefefe);
   }

   .control-element :global(.selector >  .option:not(.selected):hover) {
      border: 1px solid var(--main-color1, #6eb8ff);
      background-color: var(--main-color1-light, #6eb8ff20);
      color: var(--main-color2, #4777a4);
   }

   .control-element :global(.selector >  .option:not(.selected):hover > svg) {
      stroke: var(--main-color2, #4777a4);
   }

   /* file selector */
    .control-element :global(.file-selector::before) {
      color: var(--text-color-dark, #606570);
   }

   .control-element :global(.file-selector.selected::before) {
      color: var(--main-color1, #6eb8ff)
   }

   .control-element :global(.file-selector:focus-visible) {
      outline-color: var(--outline-color, #ccc);
   }

   .control-element :global(.file-selector:focus-visible::before) {
      outline-color: var(--outline-color, #ccc);
      color: var(--main-color1, #6eb8ff)
   }

   .control-element :global(.file-selector:hover::before) {
      color: var(--main-color1, #6eb8ff)
   }

   /* text input */
   .control-element :global(.textinput > input) {
      background-color: var(--main-color1, #6eb8ff);
      color: var(--text-color-light, #fefefe);
   }

   .control-element :global(.textinput > input:placeholder-shown) {
      color: var(--text-color-dark, #a0a0a0);
      background-color: var(--bg-color-light, #f0f0f0);
      border-color: var(--bg-color-light, #f0f0f0);
   }

   .control-element :global(.textinput > input::placeholder) {
      color: #a0a0a0;
   }

   .control-element :global(.textinput > input:active::placeholder),
   .control-element :global(.textinput > input:focus::placeholder) {
      color: transparent;
   }

   .control-element :global(.textinput > input:focus),
   .control-element :global(.textinput > input:active) {
      border-color: var(--main-color1, #6eb8ff);
      background-color: var(--main-color1-light, #6eb8ff20);
      color: var(--main-color2, #4777a4);
   }

   .control-element :global(.textinput.error > input),
   .control-element :global(.textinput.error > input:focus),
   .control-element :global(.textinput.error > input:active) {
      color: var(--warning-color, crimson);
      background-color: var(--warning-color-light, #ffd5ce);
      border-color: var(--warning-color, crimson);
   }

   .control-element :global(.textinput.error > .error-message) {
      color: var(--warning-color, crimson);
      background-color: var(--warning-color-light, #ffd5ce);
   }

   /* spinner */
   .control-element :global(.spinner:after) {
      border-color: var(--main-color1, #6eb8ff) transparent var(--main-color1, #6eb8ff) transparent;
   }

   /* number container */
   .control-element :global(.number-container) {
      background-color: var(--bg-color-light, #f0f0f0);
   }

   .control-element :global(.number-container > .value) {
      color: var(--text-color-dark, #606570);
   }

   .control-element :global(.number-container > .button-small > svg > polygon) {
      fill: var(--main-color2, #4777a4);
   }

   .control-element :global(.number-container > .button-small:hover > svg > polygon) {
      fill: var(--main-color1, #6eb8ff);
   }

   .control-element :global(.number-container:focus-visible) {
      outline-color: var(--outline-color, #ccc);
   }

</style>
